/* eslint-disable @typescript-eslint/no-floating-promises */
import type Store from '@ember-data/store';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import WrButton from 'ember-wiser/components/button';
import transitionTo from 'parog-web/helpers/transition-to';
import type DeseaseHistoryJournalEvent from 'parog-web/models/desease-history-journal-event';
import type Patient from 'parog-web/models/patient';
import type FetcherService from 'parog-web/services/fetcher';
import { sortBy } from 'parog-web/utils/sort';
import NoContentMessage from 'ui-kit/components/no-content-message';
import CardEventComponent from './card-event-component';

interface Signature {
  Args: {
    patient: Patient;
  };
}

export function deseaseHistoryJEsOldestFirst(
  patient: Patient
): DeseaseHistoryJournalEvent[] {
  return patient.deseaseHistoryJournalEvents
    .slice()
    .sort(sortBy('occurredAt', 'asc'));
}

export function mostRecentDeseaseHistoryJE(
  patient: Patient
): DeseaseHistoryJournalEvent | undefined {
  return deseaseHistoryJEsOldestFirst(patient).at(-1);
}

export default class DeseaseHistoryComponent extends Component<Signature> {
  @service declare fetcher: FetcherService;
  @service declare store: Store;

  @tracked private currentLoadedPage = 1;
  @tracked private totalPageCount?: number;

  get events(): DeseaseHistoryJournalEvent[] {
    return deseaseHistoryJEsOldestFirst(this.#patient).filter(je => je.id);
  }

  get hasMoreEventsToLoad(): boolean {
    return (
      Boolean(this.totalPageCount) &&
      this.currentLoadedPage <= this.totalPageCount!
    );
  }

  #pageSize = 15;

  get #patient(): Patient {
    return this.args.patient;
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);

    this.currentLoadedPage =
      Math.floor(
        this.args.patient.deseaseHistoryJournalEvents.length / this.#pageSize
      ) || 1;
    this.fetchDeseaseHistoryJesTask.perform();
  }

  fetchDeseaseHistoryJesTask = task(async () => {
    await this.#fetchDeseaseHistoryJes();
  });

  async #fetchDeseaseHistoryJes() {
    const response = (await this.store.query('desease-history-journal-event', {
      filter: {
        patient_id: this.#patient.id
      },
      include: ['creator'].join(','),
      page: {
        number: this.currentLoadedPage || 1,
        size: this.#pageSize
      },
      sort: 'occurred_at'
    })) as unknown as {
      meta: { 'record-count': string; 'page-count': string };
    };

    this.currentLoadedPage++;
    this.totalPageCount = parseInt(response.meta?.['page-count']) || undefined;
  }

  <template>
    <div class='flex justify-end'>
      <WrButton
        @variant='outlined'
        @icon='Plus'
        {{on
          'click'
          (transitionTo
            'medics-web.protected.patients.patient.dashboard.medical-history.desease-event.new'
          )
        }}
        data-test-add-event-button
      >
        Ajouter un événement
      </WrButton>
    </div>
    {{#each this.events as |event|}}
      <CardEventComponent @journalEvent={{event}} />
    {{else}}
      {{#unless this.fetchDeseaseHistoryJesTask.isRunning}}
        <NoContentMessage @alignment='left'>
          <span>
            Aucun événement.
          </span>
        </NoContentMessage>
      {{/unless}}
    {{/each}}

    {{#if this.fetchDeseaseHistoryJesTask.isRunning}}
      <div
        class='ui small active loader inline [&.ui.loader&]:w-full [&.ui.loader]:mx-0 [&.ui.loader]:my-[1rem]'
      ></div>
    {{else if this.hasMoreEventsToLoad}}
      <div class='my-8'>
        <button
          type='button'
          class='text-grey'
          data-test-load-more-events
          {{on 'click' (perform this.fetchDeseaseHistoryJesTask)}}
        >
          <i class='angle double down icon' />Charger plus d'événements
        </button>
      </div>
    {{/if}}
  </template>
}
